import React, {Dispatch, ReactElement, useCallback, useEffect, useState, useRef} from 'react';
import {PictureSortType} from './AddPicturesButton';
import {useTranslation} from 'react-i18next';
import {
  AssetMetaInterface,
  DesignerSettings,
  AssetService,
} from '@taopix/taopix-core/dist/types/types';
import {Button, LoadingLosenge, PictureIcon, Theme, ThemeName} from '@taopix/taopix-design-system';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import AssetServiceNavBar from './Nav/AssetServiceNavBar';
import ServiceAuth, {AuthError} from './Auth/ServiceAuth';
import {ExternalAssetMeta} from '../../Utils/Asset';
import useHistory from '../../Hooks/AssetService/useHistory';
import useExternalServiceAPI from '../../Hooks/AssetService/useExternalServiceAPI';
import FolderView from './Views/FolderView';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import MediaGallery from '../MediaGallery/MediaGallery';
import {useDispatch} from 'react-redux';
import {ActionType} from 'typesafe-actions';
import {AssetServiceActions, AssetStatus, Common} from '@taopix/taopix-core';
import useSettings from '../../Hooks/useSettings';
import {useAssetServices} from '../Context/AssetServices/AssetServicesContext';
import {AssetServicesActions} from '../Context/AssetServices/AssetServicesActions';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import GoogleDialogInline from './GoogleDialog/GoogleDialogInline';

export type RemoteAssetServiceProps = {
  isOpen: boolean;
  assetService: AssetService;
  designerSettings: DesignerSettings;
  server: APIServer;
  projectImageList: AssetMetaInterface[];
  closeEvent: () => void;
};

export type ExternalAssetAlbum = {
  id: string;
  imgSrc: string | ReactElement;
  name: string;
  onSelectAlbum: Function;
  immutable?: boolean;
  llPath?: string;
};

export type ExternalAlbumResponse = {
  data: {
    albums: ExternalAssetAlbum[];
    nextPageToken: string;
  };
  error: AuthError;
};

export type ExternalAssetResponse = {
  data: {
    photos: ExternalAssetMeta[];
    nextPageToken: string;
  };
  error: AuthError;
};

export type CreateSessionResponse = {
  session: {
    id: string;
    pickerUri: string;
    pollingConfig: {
      pollInterval: string;
      timeoutIn: string;
    };
    mediaItemsSet: boolean;
  };
  qrCode: string;
  error: AuthError;
};

const storePollingInterval = (pollRef: NodeJS.Timeout) => {
  let pollingInterval = sessionStorage?.getItem('pollingInterval') ?? '';
  if (pollingInterval !== '') {
    pollingInterval += ',';
  }
  pollingInterval += pollRef.toString();
  sessionStorage?.setItem('pollingInterval', pollingInterval);
};

/**
 * Is this google photos
 *
 * @param service
 * @returns boolean
 */
const isGooglePhotos = (service: AssetService): boolean => {
  if (!service) {
    return false;
  }
  return ['%GOOGLEPHOTOS%'].includes(service.code);
};

const RemoteAssetService = ({
  isOpen,
  closeEvent,
  assetService,
  designerSettings,
  server,
  projectImageList,
  ...props
}: RemoteAssetServiceProps) => {
  // grab the app state
  const ui = useUiState();
  const settings = useSettings();
  const services = useAssetServices();

  const {t} = useTranslation(['picturemanager']);

  const [albums, setAlbums] = useState<ExternalAssetAlbum[]>([]);
  const [selectedAlbumsPhotos, setSelectedAlbumPhotos] = useState<Partial<AssetMetaInterface>[]>(
    []
  );
  const [selectedAlbumId, setSelectedAlbumId] = useState<string>('');
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [nextAlbumPageToken, setNextAlbumPageToken] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [history, setHistory, back] = useHistory<ExternalAssetAlbum>();
  const [loading, setLoading] = useState(false);
  const [googleDialogState, setGoogleDialogState] = useState({open: false, data: {}});
  const [googleWindowHandle, setGoogleWindowHandle] = useState<any>(null);

  const api = useExternalServiceAPI(server);

  // create the action dispatcher for the asset service
  const dispatch = useDispatch<Dispatch<ActionType<typeof AssetServiceActions>>>();

  // use a ref to hold our timeout
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // set the initial album, which is all images
    setHistory([
      {
        id: '',
        imgSrc: <PictureIcon className="h-42 w-auto block m-auto" />,
        name: Common.getCurrentLocaleString(assetService.name, true),
        onSelectAlbum: getPhotosFromAlbum,
        immutable: true,
      },
    ]);

    if (assetService.accesstoken !== '') {
      // clear any errors
      setError('');

      setNextPageToken('');

      switch (assetService.code) {
        case '%GOOGLEPHOTOS%':
          createSession();
          break;

        case '%FLICKR%':
        case '%FACEBOOK%':
          getAlbums();
          break;

        default:
          setSelectedAlbumId('me');
          break;
      }
    }
  }, [assetService.accesstoken]);

  // when the selected album changes, get the photos from that album
  useEffect(() => {
    if (error.length) {
      setError('');
    }

    if (selectedAlbumId === '' && !isGooglePhotos(assetService)) {
      return;
    }

    setNextPageToken('');

    // clear the albums
    setSelectedAlbumPhotos([]);

    getPhotosFromAlbum(selectedAlbumId);
  }, [selectedAlbumId]);

  const onNavigate = (levels = 1) => {
    let lastAlbum = back(levels);
    if (null === lastAlbum) {
      lastAlbum = {id: '', imgSrc: '', name: '', onSelectAlbum: () => {}};
    }

    setNextPageToken('');
    setSelectedAlbumPhotos([]);
    setSelectedAlbumId(lastAlbum.id);
  };

  /**
   * Set the access token and authenticated state
   *
   * @param authenticated
   * @param accessToken
   */
  const onAuthenticate = (authenticated: boolean, accessToken: string) => {
    dispatch(
      AssetServiceActions.updateAssetService({
        ...assetService,
        accesstoken: accessToken,
      })
    );
  };

  /**
   * Clear the access token and authenticated state
   */
  const onLogout = () => {
    ui.dispatch(UserInterfaceActions.setIsBusy(true));

    api
      .revokeToken({
        serviceCode: assetService.code.replace(/%/g, ''),
        accessToken: assetService.accesstoken,
      })
      .then(response => {
        dispatch(
          AssetServiceActions.updateAssetService({
            ...assetService,
            accesstoken: '',
          })
        );
        // clear the albums
        setSelectedAlbumId('');
        setNextAlbumPageToken('');
        setNextPageToken('');
        setSelectedAlbumPhotos([]);
        setAlbums([]);
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      })
      .catch(error => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });

    services.dispatch(AssetServicesActions.setSelectedAssets([]));
  };

  const merge = (a, b) => {
    const seenRefs = new Set(a.map(el => el.ref));
    const c = [...a];

    for (let i = 0; i < b.length; i++) {
      const bItem = b[i];
      if (!seenRefs.has(bItem.ref)) {
        c.push(bItem);
        seenRefs.add(bItem.ref);
      }
    }

    return c;
  };

  const onSetPhotos = (payload: ExternalAssetResponse) => {
    if (payload.error.code) {
      return setError(payload.error.fullMessage);
    }

    const newArray = merge(selectedAlbumsPhotos, payload.data.photos);

    if (nextPageToken !== payload.data.nextPageToken) {
      setNextPageToken(payload.data.nextPageToken);
    }

    setSelectedAlbumPhotos(newArray);
  };

  const getPhotosFromAlbum = (albumId: string, back = false, sessionId = '') => {
    setLoading(true);
    if (assetService.accesstoken !== '') {
      api
        .getPhotos({
          assetservice: JSON.stringify({...assetService, authenticationparam1: sessionId}),
          accessToken: assetService.accesstoken,
          folderId: albumId,
          nextPageToken: albumId !== selectedAlbumId ? '' : nextPageToken,
          groupCode: designerSettings.keySettings.groupCode,
          libraryLinkPath: '',
          libraryLinkSearchTerm: '',
          serviceCode: assetService.code.replace(/%/g, ''),
        })
        .then(response => {
          if (response.error.code) {
            onError(response.error.fullMessage);
          } else {
            onSetPhotos(response);
          }
          if (
            !isGooglePhotos(assetService) ||
            (isGooglePhotos(assetService) && response.data.photos.length > 0)
          ) {
            setLoading(false);
          }
          return response.data.nextPageToken;
        })
        .then(nextPageToken => {
          if (sessionId !== '' && nextPageToken === '') {
            deleteSession(sessionId);
          }
        })
        .catch(error => {
          onError(t('assetservices:str_ErrorAssetServiceAuthentication'));
          setLoading(false);
          onLogout();
        });
    } else {
      setLoading(false);
      onLogout();
    }
  };

  const taopixAlbumData: ExternalAssetAlbum = {
    id: 'me',
    imgSrc: <PictureIcon className="h-42 w-auto block m-auto" />,
    name: t('str_ButtonAllPhotos', {
      ns: 'picturemanager',
    }),
    onSelectAlbum: getPhotosFromAlbum,
  };

  const propComparator = (prop: string) => {
    return (a: any, b: any) => {
      return a[prop] < b[prop] ? -1 : a[prop] > b[prop] ? 1 : 0;
    };
  };

  const displayAlbums = (payload: ExternalAlbumResponse) => {
    if (payload.error.code) {
      return setError(payload.error.fullMessage);
    }

    const {albums: newAlbums} = payload.data;
    const existingArray = [...albums];

    // remove the all photos album
    if (existingArray.length > 0) {
      const index = existingArray.findIndex(x => x.id === taopixAlbumData.id);
      if (index > -1) {
        existingArray.splice(index, 1);
      }
    }

    const res = existingArray.concat(
      newAlbums.map(a => {
        return {
          ...a,
          onSelectAlbum: onClickAlbum,
        };
      })
    );

    res.sort(propComparator('name'));

    if (nextAlbumPageToken !== payload.data.nextPageToken) {
      setNextAlbumPageToken(payload.data.nextPageToken);
    }

    //add the all photos album to the beginning
    res.unshift(taopixAlbumData);
    setAlbums(res);
  };

  const getAlbums = () => {
    setLoading(true);
    api
      .getAlbums({
        assetservice: JSON.stringify(assetService),
        accessToken: assetService.accesstoken,
        nextPageToken: nextAlbumPageToken,
        serviceCode: assetService.code.replace(/%/g, ''),
      })
      .then(response => {
        displayAlbums(response);
      })
      .catch(error => {
        setLoading(false);
        onError(t('assetservices:str_ErrorAssetServiceAuthentication'));
        onLogout();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClickAlbum = (album: ExternalAssetAlbum) => {
    const historyStack: ExternalAssetAlbum[] = [
      ...history,
      {
        ...album,
        onSelectAlbum: () => {},
      },
    ];

    // set the new history stack
    setHistory(historyStack);

    // reset the next page token
    setNextPageToken('');

    // set the selected album id
    setSelectedAlbumId(String(album.id));
  };

  const onError = (error: any) => {
    setError(error);
  };

  const nextPagePhotos = () => {
    if (nextPageToken !== '') {
      getPhotosFromAlbum(selectedAlbumId);
    } else {
      console.error('no next page token');
    }
  };

  const nextPageAlbums = () => {
    if (nextAlbumPageToken !== '') {
      getAlbums();
    }
  };

  /**
   * When selected, if the asset has no file size, perform
   * a head request to get the content length, and convert
   * the record into a base asset
   *
   * @param checked
   * @param pic
   */
  const onSelectAsset = useCallback(
    (checked: boolean, pic: ExternalAssetMeta) => {
      const {accesstoken, code} = assetService;

      const assets = new Map<number, ExternalAssetMeta>();
      services.state.assets.forEach(a => {
        assets.set(a.id, a as unknown as ExternalAssetMeta);
      });

      const asset = {
        ...pic,
        accessToken: accesstoken,
        serviceCode: code,
      };

      const id = asset.id as number;
      if (checked) {
        if (!assets.has(id)) {
          assets.set(id, asset);
        }
      } else {
        if (assets.has(id)) {
          assets.delete(id);
        }
      }

      services.dispatch(
        AssetServicesActions.setSelectedAssets(
          Array.from(assets.values() as unknown as AssetMetaInterface[])
        )
      );
    },
    [services.state.assets, assetService]
  );

  // TODO: move this into it's own component
  const Error = ({error}: {error: string}) => {
    if (error) {
      return (
        <Theme name={ThemeName.Warning} className="px-sm py-xs mt-sm">
          {error}
        </Theme>
      );
    } else {
      return <></>;
    }
  };

  // if we are not authenticated, show the auth view
  if (assetService.accesstoken === '') {
    return (
      <div
        className={
          'flex flex-auto flex-col justify-center items-center bg-containedStyle dark:bg-containedStyle'
        }
      >
        <Error error={error} />
        <ServiceAuth
          server={server}
          designerSettings={designerSettings}
          isOpen={false}
          closeEvent={() => {}}
          assetService={assetService}
          onAuthenticate={onAuthenticate}
          storePollingInterval={storePollingInterval}
        />
      </div>
    );
  }

  const googleAddPics = (open: boolean, data: CreateSessionResponse | {}) => {
    setGoogleDialogState({
      open: open,
      data: data,
    });
  };

  const createSession = () => {
    setLoading(true);
    server
      .post<CreateSessionResponse>(
        `/api/assetservice/${assetService.code.replaceAll('%', '')}/createSession`,
        {
          accessToken: assetService.accesstoken,
        },
        {token: 'token'}
      )
      .then((response: {data: CreateSessionResponse}) => {
        if (response.data.error.code) {
          console.error(error);
          setLoading(false);
          onError(t('assetservices:str_ErrorAssetServiceAuthentication'));
          onLogout();
        } else {
          sessionStorage?.setItem('googlePhotosToken', assetService.accesstoken);
          setLoading(false);
          googleAddPics(true, response.data);
          startSessionPoll(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        setLoading(false);
        onLogout();
      });
  };

  const randomString = () => {
    return Math.random().toString(36);
  };

  const target = `PickerWindowOpener_${assetService.code}_${randomString()}}`;

  const openPhotoPicker = (response: CreateSessionResponse | {}) => {
    setLoading(true);

    const popupWindow: Window | null = window.open(
      (response as CreateSessionResponse).session.pickerUri ?? '',
      target,
      'toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=650,height=750'
    );

    setGoogleWindowHandle(popupWindow);
  };

  const convertToMilliseconds = (str: string) => {
    const match = str.match(/^(\d+)([a-zA-Z]+)$/);

    if (match) {
      const value = parseInt(match[1], 10);
      const unit = match[2];
      switch (unit) {
        case 's':
          return value * 1000;
        case 'ms':
          return value;
        case 'm':
          return value * 60 * 1000;
        case 'h':
          return value * 60 * 60 * 1000;
        default:
          return null;
      }
    } else {
      return null;
    }
  };

  const startSessionPoll = (response: CreateSessionResponse) => {
    const interval = convertToMilliseconds(response.session.pollingConfig.pollInterval) ?? 5000;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      pollForSession(response);
    }, interval);
    storePollingInterval(intervalRef.current);
  };

  const pollForSession = (response: CreateSessionResponse) => {
    server
      .post<CreateSessionResponse>(
        '/api/assetservice/' + assetService.code.replaceAll('%', '') + '/pollSession',
        {
          sessionId: response.session.id,
          accessToken: assetService.accesstoken,
        },
        {token: 'token'}
      )
      .then((response: {data: CreateSessionResponse}) => {
        if (response.data.session.mediaItemsSet) {
          clearInterval(intervalRef.current);
          googleAddPics(false, {});
          getPhotosFromAlbum('', false, response.data.session.id);
          if (googleWindowHandle) {
            googleWindowHandle.close();
          }
        } else if ((response.data.session.pollingConfig.timeoutIn ?? '0') === '0') {
          clearInterval(intervalRef.current);
          onLogout();
        }
      })
      .catch(error => {
        clearInterval(intervalRef.current);
        console.error(error);
        setLoading(false);
      });
  };

  const deleteSession = (sessionId: string) => {
    server
      .post<CreateSessionResponse>(
        '/api/assetservice/' + assetService.code.replaceAll('%', '') + '/deleteSession',
        {
          sessionId: sessionId,
          accessToken: assetService.accesstoken,
        },
        {token: 'token'}
      )
      .catch(error => {
        console.error(error);
      });
  };

  const addMore = () => {
    setSelectedAlbumPhotos([]);
    createSession();
  };

  const onSelectGroup = (checked: boolean, payload: any[]) => {
    // create a map of the current selected assets
    const selected = new Map<number, AssetMetaInterface>();
    services.state.assets.forEach(a => {
      selected.set(a.id, a);
    });

    payload.forEach(pic => {
      if (AssetStatus.Error === pic.status) {
        return;
      }
      if (checked) {
        if (!selected.has(pic.id)) {
          selected.set(pic.id, pic);
        }
      } else {
        if (selected.has(pic.id)) {
          selected.delete(pic.id);
        }
      }
    });
    services.dispatch(AssetServicesActions.setSelectedAssets(Array.from(selected.values())));
  };

  return (
    <>
      <AssetServiceNavBar
        searchMode={false}
        searchFunction={() => {}}
        authenticated={assetService.accesstoken !== ''}
        currentAlbumId={selectedAlbumId}
        assetService={assetService}
        goHome={() => {}}
        goBack={onNavigate}
        goUserProfile={() => {}}
        albums={history}
        onClickAlbum={getPhotosFromAlbum}
        assetServiceLogout={onLogout}
      />
      <Error error={error} />
      {['', '-1'].includes(String(selectedAlbumId)) && !isGooglePhotos(assetService) ? (
        <>
          <FolderView
            nextPageToken={nextAlbumPageToken}
            albums={albums}
            loading={loading}
            onClick={onClickAlbum}
            onNextPage={nextPageAlbums}
          />
          {'' === nextPageToken && loading && (
            <div className={'flex flex-1 justify-center items-center'}>
              <LoadingLosenge />
            </div>
          )}
        </>
      ) : (
        <div className={'overflow-auto'}>
          <GoogleDialogInline
            open={googleDialogState.open}
            isGoogle={isGooglePhotos(assetService)}
            openWindow={openPhotoPicker}
            addMore={addMore}
            selectedPhotosCount={selectedAlbumsPhotos.length}
            data={googleDialogState.data}
          />
          {'' === nextPageToken && loading && (
            <div className={'flex flex-1 justify-center items-center'}>
              <LoadingLosenge />
            </div>
          )}
          <MediaGallery
            selectedImages={services.state.assets.map(a => a.id)}
            disabledAssetIds={
              (projectImageList
                .map(asset => {
                  if (asset.externalId !== '') {
                    return asset.externalId;
                  }
                  return null;
                })
                .filter(a => a) as string[]) || []
            }
            groupedView={true}
            selectableImages={true}
            onSelectPicture={onSelectAsset}
            onSelectGroup={onSelectGroup}
            hideUsed={false}
            thumbnailLayout={
              assetService.code === '%INSTAGRAM%' ? 'square-tiles' : 'full-frame-tiles'
            }
            columns={
              assetService.code === '%INSTAGRAM%' ? (ui.state.isSmallScreen ? 4 : 6) : undefined
            }
            sortBy={PictureSortType.DateTaken}
            sortDirection={isGooglePhotos(assetService) ? 'asc' : 'desc'}
            imageList={selectedAlbumsPhotos as any}
            pictureUsage={{}}
            isExternalService
            currencyCode={settings.state.currency.code}
          />
          {'' !== nextPageToken && (
            <div className={'flex flex-1 justify-center items-center'}>
              <Button
                buttonStyle={!loading ? 'distinct' : 'tertiary'}
                className={'self-center'}
                label={
                  !loading ? t('helptips:str_ButtonLoadMore') : t('*:str_LabelLoadingEllipsis')
                }
                disabled={loading}
                loading={loading}
                onClick={nextPagePhotos}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default RemoteAssetService;
